/**
 * Step 2: People Details
 * Captures personal information for each person in the plan
 */

import { useState } from 'react';
import { useForm, UseFormRegister, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Step2FormData, Step2Schema, type AllStepFormData, type PersonData } from '../../types/wizard';
import { useCurrencyInput } from '../../hooks/useCurrencyInput';
import { getStatePensionAge, STATE_PENSION_ANNUAL, STATE_PENSION_TAX_YEAR } from '../../constants/retirementBenchmarks';
import { priorIncomeAppliesToPlanYear as computePriorIncomeAppliesToPlanYear } from '../../utils/taxYear';
import { seedPriorDrawdownIncome, sumPriorIncomeBreakdown, stripDerivedPriorIncomeTotal } from '../../utils/priorIncomeSeed';
import { sanitizeNaN } from '../../utils/sanitizeNaN';
import { formatCurrency } from '../../utils/formatters';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP2_TOUR } from '../TourGuide/tourSteps';
import './Step2People.css';

/** Whole-pound default for the forecast field: the full new State Pension for the current tax year. */
const DEFAULT_STATE_PENSION_ANNUAL = Math.round(STATE_PENSION_ANNUAL);
const DEFAULT_STATE_PENSION_LABEL = `£${DEFAULT_STATE_PENSION_ANNUAL.toLocaleString('en-GB')} for ${STATE_PENSION_TAX_YEAR}`;

interface Step2Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step2FormData) => void;
  onSave: (data: Step2FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

// Helper function to calculate age
function calculateAge(dateOfBirth: string): number {
  if (!dateOfBirth) return 0;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// Helper function to calculate retirement age
function calculateRetirementAge(dateOfBirth: string, retirementDate: string): number {
  if (!dateOfBirth || !retirementDate) return 0;
  const birthDate = new Date(dateOfBirth);
  const retirement = new Date(retirementDate);
  let age = retirement.getFullYear() - birthDate.getFullYear();
  const monthDiff = retirement.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && retirement.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// Separate component for currency inputs to safely use hooks
interface CurrencyFieldProps {
  id: string;
  value: number | undefined;
  registerProps: ReturnType<UseFormRegister<Step2FormData>>;
  error?: string;
  onBlurCallback: () => void;
  className?: string;
}

function CurrencyField({ id, value, registerProps, error, onBlurCallback, className }: Readonly<CurrencyFieldProps>) {
  const currencyProps = useCurrencyInput({
    value,
    registerProps,
  });

  return (
    <input
      id={id}
      type="text"
      className={`form-input ${error ? 'error' : ''} ${className || ''}`}
      {...currencyProps}
      onBlur={(e) => {
        currencyProps.onBlur(e);
        onBlurCallback();
      }}
    />
  );
}

/**
 * Read the numeric value out of the change event useCurrencyInput relays to
 * registerProps.onChange (target is a plain object it builds itself, not a
 * real DOM node — see useCurrencyInput.ts's handleChange). react-hook-form
 * types ChangeHandler's event.target as `any`; narrow it explicitly here
 * rather than accessing properties on `any` directly (no-unsafe-member-access).
 */
function currencyChangeValue(event: { target: unknown }): number {
  const target = event.target as { valueAsNumber?: unknown; value?: unknown };
  const asNumber = target.valueAsNumber;
  if (typeof asNumber === 'number' && !Number.isNaN(asNumber)) return asNumber;
  const raw = target.value;
  const parsed = typeof raw === 'string' ? Number.parseFloat(raw) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * The four prior-income boxes need a running total that updates as the user
 * types (Task C1). react-hook-form's own reactivity does not work for these
 * fields, and the root cause is deeper than a missed notification.
 *
 * useCurrencyInput's handleChange builds the event it forwards to
 * registerProps.onChange by spreading the *real* DOM event's target:
 * `{ ...e.target, value, valueAsNumber }`. Spreading a DOM element only
 * copies its own enumerable properties — `name`, `value` etc. live on
 * HTMLInputElement's prototype as getters, not as own properties — so the
 * spread silently drops `.name`. react-hook-form's registered onChange needs
 * `event.target.name` to look up which field to update; without it, the
 * call is a no-op. Verified empirically (render a field through
 * useCurrencyInput, fire a change, then read both useWatch's value and an
 * imperative getValues() immediately after): both stay at the pre-change
 * value. Neither the internal store nor watch subscribers see the
 * keystroke. It only catches up on blur, because useCurrencyInput's
 * handleBlur forwards the *real*, un-spread event straight to
 * registerProps.onBlur, and that path does have a working `.name`.
 *
 * A running total that must update per keystroke can't wait for blur, so it
 * needs its own plain React state kept in lockstep by this wrapper rather
 * than depending on react-hook-form's reactivity for display.
 */
function withLocalAmountSync(
  registerProps: ReturnType<UseFormRegister<Step2FormData>>,
  setLocal: (value: number) => void
): ReturnType<UseFormRegister<Step2FormData>> {
  return {
    ...registerProps,
    onChange: (event) => {
      setLocal(currencyChangeValue(event));
      return registerProps.onChange(event);
    },
  };
}

/** Standard LSA cap for 2024/25, frozen until April 2028 */
const DEFAULT_LSA_CAP = 268275;

// Separate component for each person form to safely use hooks
interface PersonFormProps {
  personIndex: number;
  planType: string;
  personData: Partial<PersonData> | undefined;
  register: UseFormRegister<Step2FormData>;
  errors: FieldErrors<Step2FormData>;
  handleBlur: () => void;
  planAsOfDate?: string;
}

function PersonForm({ personIndex, planType, personData, register, errors, handleBlur, planAsOfDate }: Readonly<PersonFormProps>) {
  // Derive LSA state once for use in conditional hints
  const lsaUsed = personData?.lsaUsedPrePlan ?? 0;
  const lsaCap = personData?.taxFreeLumpSumCap ?? DEFAULT_LSA_CAP;
  // Prior income fields (salary, drawdown, State Pension, rental, dividends,
  // pension contributions) only matter when the plan start date falls in the
  // same UK tax year the engine bands as Year 1. A plan starting 1 Jan-5 Apr
  // means anything received before it sits in the tax year that began the
  // PREVIOUS April, while engine year 1 is banded as the one beginning the
  // COMING April. The engine drops the seed in that case
  // (calculatePriorTaxYearIncome), so offering inputs that silently do
  // nothing would be worse than offering none. See taxYear.ts for the shared
  // day-accurate boundary logic (also used by the mobile wizard variant).
  const priorIncomeAppliesToPlanYear = computePriorIncomeAppliesToPlanYear(planAsOfDate);
  const showPriorIncomeFields = Boolean(planAsOfDate && priorIncomeAppliesToPlanYear);
  const remainingAllowance = lsaCap - lsaUsed;

  // The engine only takes ONE non-dividend prior-income field
  // (priorOtherTaxableIncome) — salary, drawdown, State Pension and rental
  // are all ordinary non-savings income taxed identically, so splitting the
  // engine four ways would just add fields that behave the same (YAGNI). But
  // asking users to mentally sum four figures before typing one number
  // offloads arithmetic onto them and a wrong subtotal silently mis-bands
  // their Personal Allowance — so the WIZARD collects the four amounts
  // separately and sums them here (sumPriorIncomeBreakdown,
  // priorIncomeSeed.ts). Dividends are kept apart because they route through
  // a different allowance (the £500 dividend nil-rate band) and map straight
  // onto their own payload field, priorDividendIncome.
  //
  // Plain React state (kept current by withLocalAmountSync's onChange
  // wrapper), not react-hook-form's watch/useWatch — see
  // withLocalAmountSync's comment: react-hook-form's own store/watchers
  // don't update at all for these fields until blur. Seeded once from
  // personData at mount, same as every other field's initial value in this
  // form.
  const [priorSalaryIncome, setPriorSalaryIncome] = useState(personData?.priorSalaryIncome ?? 0);
  const [priorDrawdownIncome, setPriorDrawdownIncome] = useState(personData?.priorDrawdownIncome ?? 0);
  const [priorStatePensionIncome, setPriorStatePensionIncome] = useState(personData?.priorStatePensionIncome ?? 0);
  const [priorRentalIncome, setPriorRentalIncome] = useState(personData?.priorRentalIncome ?? 0);
  const [priorDividendIncome, setPriorDividendIncome] = useState(personData?.priorDividendIncome ?? 0);
  // Shared, NaN-safe formula (priorIncomeSeed.ts) — this exact `a + b + c + d`
  // used to be hand-copied here, in the mobile variant, and in
  // UpdatePlanFlow.tsx; see priorIncomeSeed.ts's file comment for why that
  // drifted into a bug on the mobile copy (HIGH 1, #1592 phase 2 review
  // round 3).
  const nonDividendSubtotal = sumPriorIncomeBreakdown(priorSalaryIncome, priorDrawdownIncome, priorStatePensionIncome, priorRentalIncome);
  const totalPriorIncome = nonDividendSubtotal + priorDividendIncome;

  // No sync effect writing this running total into `priorOtherTaxableIncome`
  // form state any more (phase 3, derived-total refactor) — that field is
  // never submitted or persisted at all. The four breakdown boxes above are
  // the only thing saved; the server derives the combined total itself
  // (PersonSchema in projectionController.ts), and every client-side
  // surface that displays or diffs the total derives it too (see
  // deriveTotalPriorOtherIncome, priorIncomeSeed.ts). Writing a derived
  // value into form state that ALSO gets persisted was exactly the
  // cache-with-no-invalidation shape behind every #1592 bug in this
  // feature — see priorIncomeSeed.ts's file comment.

  return (
    <div className="person-form">
      <h3 className="person-header">
        {planType === 'JOINT' ? `Person ${personIndex + 1}` : 'Your Details'}
      </h3>

      {/* Full Name */}
      <div className="form-group">
        <label htmlFor={`persons.${personIndex}.fullName`} className="form-label">
          Full Name <span className="required">*</span>
        </label>
        <input
          id={`persons.${personIndex}.fullName`}
          type="text"
          className={`form-input ${errors.persons?.[personIndex]?.fullName ? 'error' : ''}`}
          placeholder="e.g., Jane Smith"
          {...register(`persons.${personIndex}.fullName`)}
          onBlur={handleBlur}
        />
        {errors.persons?.[personIndex]?.fullName && (
          <span className="error-message">{errors.persons[personIndex].fullName?.message}</span>
        )}
      </div>

      {/* Date of Birth */}
      <div className="form-group">
        <label htmlFor={`persons.${personIndex}.dateOfBirth`} className="form-label">
          Date of Birth <span className="required">*</span>
        </label>
        <input
          id={`persons.${personIndex}.dateOfBirth`}
          type="date"
          className={`form-input ${errors.persons?.[personIndex]?.dateOfBirth ? 'error' : ''}`}
          {...register(`persons.${personIndex}.dateOfBirth`)}
          onBlur={handleBlur}
        />
        {errors.persons?.[personIndex]?.dateOfBirth && (
          <span className="error-message">{errors.persons[personIndex].dateOfBirth?.message}</span>
        )}
        {personData?.dateOfBirth && (
          <span className="age-display">
            Age: {calculateAge(personData.dateOfBirth)}
          </span>
        )}
      </div>

      {/* Retirement Date */}
      <div className="form-group">
        <label htmlFor={`persons.${personIndex}.retirementDate`} className="form-label">
          Planned Retirement Date <span className="required">*</span>
        </label>
        <input
          id={`persons.${personIndex}.retirementDate`}
          type="date"
          className={`form-input ${errors.persons?.[personIndex]?.retirementDate ? 'error' : ''}`}
          {...register(`persons.${personIndex}.retirementDate`)}
          onBlur={handleBlur}
        />
        {errors.persons?.[personIndex]?.retirementDate && (
          <span className="error-message">{errors.persons[personIndex].retirementDate?.message}</span>
        )}
        {personData?.dateOfBirth && personData?.retirementDate && (
          <span className="retirement-age-display">
            Retirement age: {calculateRetirementAge(personData.dateOfBirth, personData.retirementDate)}
          </span>
        )}
        <span className="help-text">Date when you plan to stop working</span>
      </div>

      {/* Prior income already received + Prior Pension Contributions — both
          seed the engine's Year 1 tax banding, so they are un-collapsed and
          shown/hidden rather than tucked behind a disclosure. The five income
          boxes below are a wizard-only convenience: asking someone to
          mentally sum salary + drawdown + State Pension + rental + dividends
          before typing one number offloads arithmetic onto them, and a wrong
          subtotal silently mis-bands their Personal Allowance. We sum salary,
          drawdown and State Pension and rental for them (see the running
          total above) and pass dividends straight through to
          priorDividendIncome, since only dividends need different tax
          treatment (the £500 dividend nil-rate band). */}
      {showPriorIncomeFields && (
        <>
          <div className="form-group prior-income-group">
            <span className="form-label">
              Total already received this tax year (£) — not a yearly rate
            </span>
            <span className="help-text">
              Enter what you've actually received since 6 April in each category below, not an
              annual rate — we use the total to work out how much of your Personal Allowance and
              basic-rate band is already used. Leave any box at £0 if not applicable.
            </span>

            <label htmlFor={`persons.${personIndex}.priorSalaryIncome`} className="form-label prior-income-sub-label">
              Salary (£)
            </label>
            <CurrencyField
              id={`persons.${personIndex}.priorSalaryIncome`}
              value={priorSalaryIncome}
              registerProps={withLocalAmountSync(register(`persons.${personIndex}.priorSalaryIncome`, { valueAsNumber: true }), setPriorSalaryIncome)}
              error={errors.persons?.[personIndex]?.priorSalaryIncome?.message}
              onBlurCallback={handleBlur}
            />
            {errors.persons?.[personIndex]?.priorSalaryIncome && (
              <span className="error-message">{errors.persons[personIndex].priorSalaryIncome?.message}</span>
            )}

            <label htmlFor={`persons.${personIndex}.priorDrawdownIncome`} className="form-label prior-income-sub-label">
              Private pension income (£)
            </label>
            <CurrencyField
              id={`persons.${personIndex}.priorDrawdownIncome`}
              value={priorDrawdownIncome}
              registerProps={withLocalAmountSync(register(`persons.${personIndex}.priorDrawdownIncome`, { valueAsNumber: true }), setPriorDrawdownIncome)}
              error={errors.persons?.[personIndex]?.priorDrawdownIncome?.message}
              onBlurCallback={handleBlur}
            />
            {errors.persons?.[personIndex]?.priorDrawdownIncome && (
              <span className="error-message">{errors.persons[personIndex].priorDrawdownIncome?.message}</span>
            )}
            <span className="help-text">
              Any taxable pension income other than the State Pension — drawdown, an annuity, a
              defined benefit (final salary) pension, or a lump sum you took as income.
            </span>

            <label htmlFor={`persons.${personIndex}.priorStatePensionIncome`} className="form-label prior-income-sub-label">
              State Pension already paid (£)
            </label>
            <CurrencyField
              id={`persons.${personIndex}.priorStatePensionIncome`}
              value={priorStatePensionIncome}
              registerProps={withLocalAmountSync(register(`persons.${personIndex}.priorStatePensionIncome`, { valueAsNumber: true }), setPriorStatePensionIncome)}
              error={errors.persons?.[personIndex]?.priorStatePensionIncome?.message}
              onBlurCallback={handleBlur}
            />
            {errors.persons?.[personIndex]?.priorStatePensionIncome && (
              <span className="error-message">{errors.persons[personIndex].priorStatePensionIncome?.message}</span>
            )}

            <label htmlFor={`persons.${personIndex}.priorRentalIncome`} className="form-label prior-income-sub-label">
              Rental income (£)
            </label>
            <CurrencyField
              id={`persons.${personIndex}.priorRentalIncome`}
              value={priorRentalIncome}
              registerProps={withLocalAmountSync(register(`persons.${personIndex}.priorRentalIncome`, { valueAsNumber: true }), setPriorRentalIncome)}
              error={errors.persons?.[personIndex]?.priorRentalIncome?.message}
              onBlurCallback={handleBlur}
            />
            {errors.persons?.[personIndex]?.priorRentalIncome && (
              <span className="error-message">{errors.persons[personIndex].priorRentalIncome?.message}</span>
            )}

            <p className="prior-income-running-total">
              Subtotal (everything except dividends): {formatCurrency(nonDividendSubtotal, true)}
            </p>

            <label htmlFor={`persons.${personIndex}.priorDividendIncome`} className="form-label prior-income-sub-label">
              Dividend income (£)
            </label>
            <CurrencyField
              id={`persons.${personIndex}.priorDividendIncome`}
              value={priorDividendIncome}
              registerProps={withLocalAmountSync(register(`persons.${personIndex}.priorDividendIncome`, { valueAsNumber: true }), setPriorDividendIncome)}
              error={errors.persons?.[personIndex]?.priorDividendIncome?.message}
              onBlurCallback={handleBlur}
            />
            {errors.persons?.[personIndex]?.priorDividendIncome && (
              <span className="error-message">{errors.persons[personIndex].priorDividendIncome?.message}</span>
            )}
            <span className="help-text">
              Kept separate from the total above — dividends use their own £500 tax-free
              allowance rather than stacking with your other income.
            </span>

            <p className="prior-income-running-total prior-income-running-total--grand">
              Total already received this tax year: {formatCurrency(totalPriorIncome, true)}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor={`persons.${personIndex}.priorPensionContributions`} className="form-label">
              Pension contributions already made this tax year (£)
            </label>
            <CurrencyField
              id={`persons.${personIndex}.priorPensionContributions`}
              value={personData?.priorPensionContributions}
              registerProps={register(`persons.${personIndex}.priorPensionContributions`, { valueAsNumber: true })}
              error={errors.persons?.[personIndex]?.priorPensionContributions?.message}
              onBlurCallback={handleBlur}
            />
            {errors.persons?.[personIndex]?.priorPensionContributions && (
              <span className="error-message">{errors.persons[personIndex].priorPensionContributions?.message}</span>
            )}
            <span className="help-text">
              Gross pension contributions already made since 6 April, including any tax
              relief added by your provider. Leave at £0 if not applicable.
            </span>
          </div>
        </>
      )}

      {planAsOfDate && !priorIncomeAppliesToPlanYear && (
        <div className="form-group">
          <span className="help-text">
            Your plan starts before 6 April, in the last few weeks of the current tax year, so
            anything received beforehand belongs to the tax year that's ending — not the one
            this plan covers. There's nothing to enter here.
          </span>
        </div>
      )}

      {/* State Pension Age */}
      <div className="form-group">
        <label htmlFor={`persons.${personIndex}.statePensionAge`} className="form-label">
          State Pension Age <span className="required">*</span>
        </label>
        <input
          id={`persons.${personIndex}.statePensionAge`}
          type="number"
          className={`form-input ${errors.persons?.[personIndex]?.statePensionAge ? 'error' : ''}`}
          {...register(`persons.${personIndex}.statePensionAge`, { valueAsNumber: true })}
          onBlur={handleBlur}
        />
        {errors.persons?.[personIndex]?.statePensionAge && (
          <span className="error-message">{errors.persons[personIndex].statePensionAge?.message}</span>
        )}
        <span className="help-text">
          {personData?.dateOfBirth
            ? `Based on your date of birth, your State Pension Age is likely ${getStatePensionAge(new Date(personData.dateOfBirth).getFullYear())}. `
            : 'When you\'ll start receiving state pension. '}
          <a href="https://www.gov.uk/check-state-pension-age" target="_blank" rel="noopener noreferrer">Check at gov.uk</a>
        </span>
      </div>

      {/* Current State Pension Annual */}
      <div className="form-group">
        <label htmlFor={`persons.${personIndex}.currentStatePensionAnnual`} className="form-label">
          Your State Pension forecast (£/year) <span className="required">*</span>
        </label>
        <CurrencyField
          id={`persons.${personIndex}.currentStatePensionAnnual`}
          value={personData?.currentStatePensionAnnual}
          registerProps={register(`persons.${personIndex}.currentStatePensionAnnual`, { valueAsNumber: true })}
          error={errors.persons?.[personIndex]?.currentStatePensionAnnual?.message}
          onBlurCallback={handleBlur}
        />
        {errors.persons?.[personIndex]?.currentStatePensionAnnual && (
          <span className="error-message">{errors.persons[personIndex].currentStatePensionAnnual?.message}</span>
        )}
        <span className="help-text">We've used the full new State Pension ({DEFAULT_STATE_PENSION_LABEL}). Update this if you know your actual forecast from gov.uk.</span>
      </div>

      {/* Pension Tax-Free Cash Group — hidden by default, most users don't need to change */}
      <details className="tax-free-cash-disclosure">
        <summary className="tax-free-cash-summary">
          Pension tax-free cash settings (most people can skip this)
        </summary>
      <fieldset className="tax-free-cash-group">
        <legend className="tax-free-cash-legend">Pension Tax-Free Cash</legend>

        {/* Lifetime Tax-Free Cash Limit */}
        <div className="form-group">
          <label htmlFor={`persons.${personIndex}.taxFreeLumpSumCap`} className="form-label">
            Lifetime Tax-Free Cash Limit (£) <span className="required">*</span>
          </label>
          <CurrencyField
            id={`persons.${personIndex}.taxFreeLumpSumCap`}
            value={personData?.taxFreeLumpSumCap}
            registerProps={register(`persons.${personIndex}.taxFreeLumpSumCap`, { valueAsNumber: true })}
            error={errors.persons?.[personIndex]?.taxFreeLumpSumCap?.message}
            onBlurCallback={handleBlur}
          />
          {errors.persons?.[personIndex]?.taxFreeLumpSumCap && (
            <span className="error-message">{errors.persons[personIndex].taxFreeLumpSumCap?.message}</span>
          )}
          <span className="help-text">
            Your personal limit on lifetime tax-free pension cash. Standard limit is £268,275 (2024/25).
            If you hold HMRC protection, enter your protected amount instead.
          </span>
        </div>

        {/* Tax-Free Pension Cash Already Taken */}
        <div className="form-group">
          <label htmlFor={`persons.${personIndex}.lsaUsedPrePlan`} className="form-label">
            Tax-Free Pension Cash Already Taken (£)
          </label>
          <CurrencyField
            id={`persons.${personIndex}.lsaUsedPrePlan`}
            value={personData?.lsaUsedPrePlan}
            registerProps={register(`persons.${personIndex}.lsaUsedPrePlan`, { valueAsNumber: true })}
            error={errors.persons?.[personIndex]?.lsaUsedPrePlan?.message}
            onBlurCallback={handleBlur}
          />
          {errors.persons?.[personIndex]?.lsaUsedPrePlan && (
            <span className="error-message">{errors.persons[personIndex].lsaUsedPrePlan?.message}</span>
          )}
          <span className="help-text">
            How much tax-free cash have you already taken from any pension? Leave at 0 if none.
          </span>
          {/* Remaining allowance hint — positioned directly below triggering field */}
          {lsaUsed > 0 && remainingAllowance > 0 && (
            <p className="remaining-allowance">
              Remaining tax-free allowance: £{remainingAllowance.toLocaleString()}
            </p>
          )}
          {lsaUsed > 0 && remainingAllowance <= 0 && (
            <p className="exhausted-allowance">
              Your full tax-free allowance has been used. Further pension withdrawals will be taxed as income.
            </p>
          )}
        </div>

        {/* Take as Tax-Free Cash % */}
        <div className="form-group">
          <label htmlFor={`persons.${personIndex}.taxFreeLumpSumPct`} className="form-label">
            Take as Tax-Free Cash (%) <span className="required">*</span>
          </label>
          <input
            id={`persons.${personIndex}.taxFreeLumpSumPct`}
            type="number"
            step="0.01"
            className={`form-input ${errors.persons?.[personIndex]?.taxFreeLumpSumPct ? 'error' : ''}`}
            {...register(`persons.${personIndex}.taxFreeLumpSumPct`, { valueAsNumber: true })}
            onBlur={handleBlur}
          />
          {errors.persons?.[personIndex]?.taxFreeLumpSumPct && (
            <span className="error-message">{errors.persons[personIndex].taxFreeLumpSumPct?.message}</span>
          )}
          <span className="help-text">
            Percentage of each pension withdrawal taken tax-free. Typically 25%, capped by your remaining lifetime allowance.
          </span>
        </div>

        <details className="lta-disclosure">
          <summary>What counts towards tax-free cash already taken?</summary>
          <p>
            Include the full amount of any pension commencement lump sum (PCLS) taken at crystallisation,
            plus 25% of each uncrystallised funds pension lump sum (UFPLS) payment — not the gross UFPLS amount.
            Check your pension statements or ask your provider for your total tax-free amount used.
          </p>
          <p>
            If you started drawdown before 6 April 2024, your remaining allowance may differ under
            HMRC transitional rules — check with your pension administrator.
          </p>
        </details>
      </fieldset>
      </details>

    </div>
  );
}

function Step2People({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step2Props>) {
  const planType = initialData?.planType || 'SINGLE';
  const numPersons = planType === 'JOINT' ? 2 : 1;

  // Create default persons array
  const defaultPersons = Array.from({ length: numPersons }, (_, i) => ({
    fullName: initialData?.persons?.[i]?.fullName || '',
    dateOfBirth: initialData?.persons?.[i]?.dateOfBirth || '',
    retirementDate: initialData?.persons?.[i]?.retirementDate || '',
    statePensionAge: initialData?.persons?.[i]?.statePensionAge || 67,
    currentStatePensionAnnual: initialData?.persons?.[i]?.currentStatePensionAnnual || DEFAULT_STATE_PENSION_ANNUAL,
    taxFreeLumpSumPct: initialData?.persons?.[i]?.taxFreeLumpSumPct || 25,
    taxFreeLumpSumCap: initialData?.persons?.[i]?.taxFreeLumpSumCap || DEFAULT_LSA_CAP,
    lsaUsedPrePlan: initialData?.persons?.[i]?.lsaUsedPrePlan ?? 0,
    priorSalaryIncome: initialData?.persons?.[i]?.priorSalaryIncome ?? 0,
    // No `priorOtherTaxableIncome` here (phase 3, derived-total refactor) —
    // it is never persisted or submitted from the wizard any more, so there
    // is nothing to seed it from. The engine's combined figure is always
    // derived from the breakdown below, server-side (PersonSchema in
    // projectionController.ts) and client-side wherever it's displayed
    // (deriveTotalPriorOtherIncome, priorIncomeSeed.ts).
    //
    // Wizard-only breakdown (Task C, #1592 phase 2). A plan saved before
    // this split only has the combined total — seed it into "Drawdown"
    // rather than 0 so the running total still matches what was there
    // before, instead of silently zeroing it out. seedPriorDrawdownIncome
    // distinguishes "never set" (legacy plan, seed from the total) from
    // "explicitly cleared" (stays 0) — see CRITICAL 2, #1592 phase 2 review
    // round 2: a plain `??` re-seeds from the legacy total even after the
    // user deliberately blanked this box, doubling the running total on
    // reopen. This is a one-way migration, not a drift reconciliation —
    // once the breakdown is saved, that stored total is dropped entirely
    // and never consulted again (see mergeRebaselineUpdates/PersonSchema).
    priorDrawdownIncome: seedPriorDrawdownIncome(
      initialData?.persons?.[i]?.priorDrawdownIncome,
      initialData?.persons?.[i]?.priorOtherTaxableIncome
    ),
    priorStatePensionIncome: initialData?.persons?.[i]?.priorStatePensionIncome ?? 0,
    priorRentalIncome: initialData?.persons?.[i]?.priorRentalIncome ?? 0,
    priorDividendIncome: initialData?.persons?.[i]?.priorDividendIncome ?? 0,
    priorPensionContributions: initialData?.persons?.[i]?.priorPensionContributions ?? 0,
  }));

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Step2FormData>({
    resolver: zodResolver(Step2Schema),
    defaultValues: {
      persons: defaultPersons,
    },
  });

  // Watch date of birth fields for age calculation
  const personsData = watch('persons');

  const handleBlur = () => {
    // Always save on blur - isDirty doesn't work reliably with useCurrencyInput
    const data = watch();
    // A blanked currency box leaves react-hook-form holding NaN (see
    // sanitizeNaN.ts) — scrub it to 0 here so JSON.stringify never turns it
    // into a `null` that a later reload can't tell apart from "explicitly
    // cleared" (CRITICAL 2, #1592 phase 2 review round 2).
    onSave(stripDerivedPriorIncomeTotal(sanitizeNaN(data)));
  };

  const onSubmit = (data: Step2FormData) => {
    onNext(stripDerivedPriorIncomeTotal(data));
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>{planType === 'JOINT' ? 'Tell us about yourselves' : 'Tell us about yourself'}</h2>
          <TourButton tour={WIZARD_STEP2_TOUR} variant="text" />
        </div>
        <p>We need some details to calculate your retirement projections</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown} className="step-form">
        <div className={`persons-container ${planType === 'JOINT' ? 'joint' : 'single'}`}>
          {planType === 'SINGLE' ? (
            <PersonForm
              personIndex={0}
              planType={planType}
              personData={personsData[0]}
              register={register}
              errors={errors}
              handleBlur={handleBlur}
              planAsOfDate={initialData?.planAsOfDate}
            />
          ) : (
            <>
              <PersonForm
                personIndex={0}
                planType={planType}
                personData={personsData[0]}
                register={register}
                errors={errors}
                handleBlur={handleBlur}
                planAsOfDate={initialData?.planAsOfDate}
              />
              <PersonForm
                personIndex={1}
                planType={planType}
                personData={personsData[1]}
                register={register}
                errors={errors}
                handleBlur={handleBlur}
                planAsOfDate={initialData?.planAsOfDate}
              />
            </>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {onBack && (
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit)()}>
            Next: Add Investments
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default Step2People;
